// Shared entity types and HTTP envelopes used across multiple admin service modules.
import type { ApiResponse } from '../../../../api/axios';

export type { ApiResponse };

export const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

export interface PaginatedResponse<T> {
    data: T[];
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
}

export interface PaginatedMetadata<T> {
    data: T[];
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
}

export interface Category {
    id: number;
    name: string;
    code: string;
    active: boolean;
    createdDate: string;
}

export interface GeoPoliticalZone {
    id: number;
    name: string;
    active: boolean;
}

export interface State {
    id: number;
    name: string;
    active: boolean;
    geoPoliticalZone?: GeoPoliticalZone;
}

export interface Institution {
    id: number;
    createdDate: string;
    name: string;
    active: boolean;
    isPublic: boolean;
    stateId: number;
    state?: State;
}

export interface PersonInfo {
    personId: string;
    lastName: string;
    firstName: string;
    otherName: string;
    email: string;
    phoneNo: string;
    address: string;
    active: boolean;
    createdDate: string;
    fullName: string;
    qualificationId: number;
    institutionId: number | null;
    passportUrl: string | null;
    titleId: number;
    sexId: number;
    dob: string;
    stateId: number;
    state?: State;
    rankId: number;
    profileCompleted: boolean;
    institution?: Institution;
}

export interface RoleItem {
    id: number;
    name: string;
    active: boolean;
    slug: string;
}

export interface UserRoleItem {
    id: string;
    userId: string;
    roleId: number;
    role: RoleItem;
    dateCreated: string;
    isDeactivated: boolean;
    isActive: boolean;
}

export interface UserItem {
    userId: string;
    username: string;
    lastLogin: string;
    lastPasswordChange: string;
    isVerified: boolean;
    roleId: number;
    personId: string;
    person: PersonInfo;
    role: RoleItem | null;
    roles?: UserRoleItem[];
    active: boolean;
    createdDate: string;
}

export interface EventType {
    id: number;
    name: string;
    active: boolean;
}

export interface Cycle {
    id: number;
    name: string;
    active: boolean;
    submissionReady: boolean;
    tag?: string | null;
}

export interface CycleEvent {
    id: number;
    eventTypeId: number;
    eventType: EventType;
    cycleId: number;
    cycle: Cycle;
    startDate: string;
    endDate: string;
    active: boolean;
}

export interface BookAuthor {
    id: string;
    submissionId: string;
    name: string;
    isActive: boolean;
    isLeadAuthor: boolean;
    affiliateInstitution: string;
}

export interface SubmissionVersion {
    id: string;
    manuscriptUrl: string;
    dateSubmitted: string;
    submissionId: string;
    versionNumber: number;
}

export interface Submission {
    id: string;
    submissionNumber: string;
    abstract: string;
    manuscriptUrl: string;
    isbnNo: string;
    authors: string;
    submittedAt: string;
    authorId: string;
    author: UserItem | null;
    title: string;
    status: number;
    applicationType: number;
    cycleId: number;
    cycle: Cycle | null;
    categoryId: number;
    category: Category | null;
    categoryName?: string;
    plagiarismOutcome: number;
    keywords?: string;
    edition?: string;
    volume?: string;
    publicationUrl?: string;
    issue?: string;
    isTagDivisionalHeadApproved?: boolean;
    tagDivisionalHeadApprovalDate?: string;
    isDESSApproved?: boolean;
    dessApprovalDate?: string;
    isESApproved?: boolean;
    esApprovalDate?: string;
    isReadyForProcessing?: boolean;
    currentStage?: number;
    completedPrimaFacie?: boolean;
    primaFacieDecision?: string | null;
    finalDecision?: string | null;
    workflowHistories?: Array<{
        id: string;
        submissionId: string;
        fromStage: string;
        toStage: string;
        actionType: string;
        actionAt: string;
    }>;
}

export interface Reviewer {
    reviewerId: string;
    reviewerName: string;
    isCompleted: boolean;
    isActive: boolean;
    assignedAt: string;
    reassignmentEnabledDate?: string | null;
    reReview?: boolean;
    enableReassignment?: boolean;
    review: {
        score: number | null;
        remarks: string | null;
        submittedAt: string | null;
    } | null;
}

export interface DeskItem {
    id: number;
    name: string;
}
